// src/components/Admin/Leads/SalesLeadListItem.jsx
//
// Read-only lead summary card for the Sales Call Leads list. Modeled visually
// on the specialist-facing LeadListItem (selyst-frontend-next-app), but scoped
// to admin needs: no purchase/reject actions, contact fields are always the
// backend-masked values — never attempt to unmask here.
import React from 'react';
import { Card, CardContent } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { MapPin, Clock, Globe, ShieldCheck, Coins } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '../../../lib/utils';

export default function SalesLeadListItem({ lead, isSelected, onClick }) {
  const timeSince = lead.created_at ? formatDistanceToNow(new Date(lead.created_at), { addSuffix: true }) : null;

  return (
    <Card
      onClick={onClick}
      role="button"
      tabIndex={0}
      aria-pressed={isSelected}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.();
        }
      }}
      className={cn(
        'cursor-pointer hover:border-primary transition-colors shadow-sm',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        isSelected && 'border-primary ring-2 ring-primary bg-secondary'
      )}
    >
      <CardContent className="p-3 space-y-2">
        <div className="flex justify-between items-start gap-2">
          <div className="flex items-center gap-1 min-w-0">
            <h3 className="text-base font-bold text-foreground truncate">{lead.buyer_first_name || 'Buyer'}</h3>
            {lead.is_buyer_phone_verified && (
              <ShieldCheck className="h-4 w-4 text-green-600 flex-shrink-0" aria-label="Verified phone" />
            )}
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {lead.remote_service && (
              <Badge variant="outline" className="gap-1">
                <Globe className="h-3 w-3" /> Remote
              </Badge>
            )}
            <Badge variant="secondary" className="gap-1">
              <Coins className="h-3 w-3" />
              {lead.price_coins != null ? `${lead.price_coins}` : 'N/A'}
            </Badge>
          </div>
        </div>

        <h4 className="text-sm text-foreground">{lead.category_name}</h4>

        <div className="flex items-center text-sm text-muted-foreground">
          <MapPin className="h-4 w-4 mr-2 flex-shrink-0" />
          <span className="truncate">{lead.address_summary || lead.full_address}</span>
        </div>

        <div className="border-b border-border -mx-3" />

        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>
            {lead.purchase_count} of {lead.max_purchases} sellers connected
          </span>
          {timeSince && (
            <span className="flex items-center">
              <Clock className="h-3.5 w-3.5 mr-1" />
              {timeSince}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
